// components/MessageComposer.jsx
import React, { useState, useRef, useEffect } from "react";
import { FiMessageSquare, FiEdit2, FiTrash2, FiPlus, FiCopy } from "react-icons/fi";
import { ImInsertTemplate } from "react-icons/im";
import templates from "../../data/MessageTemplates";
import { useLocation } from "react-router-dom";
import templateService from "../../api/Customer/TemplateService";
import TemplateModal from "./TemplateModal"; // Import external component
import { toast } from "react-toastify";

const MessageComposer = ({ message, setMessage, recipients = [], showVariables = false }) => {
  const [showTemplates, setShowTemplates] = useState(false);
  const [isTemplateMessageOpen, setIsTemplateMessageOpen] = useState(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const textareaRef = useRef(null);
  const location = useLocation();
  const isQuickSMS = location.pathname.includes("/quick-sms");

  const smsPartLength = 160;
  const messageLength = message.length;
  const smsParts = Math.ceil(messageLength / smsPartLength) || 1;
  const actualRecipientCount = recipients.length;
  const totalSms = actualRecipientCount * smsParts;

  // Check if ready to send
  const hasRecipients = recipients.length > 0;
  const hasMessage = message.trim().length > 0;
  const isReadyToSend = hasRecipients && hasMessage;

  // Fetch custom templates
  const fetchCustomTemplates = async () => {
    setLoading(true);
    try {
      const response = await templateService.getCustomTemplates();
      
      if (response.success && response.data?.data?.data) {
        setCustomTemplates(response.data.data.data);
      } else {
        console.error("Failed to load templates:", response.error);
        toast.error(response.error?.reason || "Failed to load custom templates");
      }
    } catch (error) {
      console.error("Error fetching custom templates:", error);
      toast.error("Failed to load custom templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showTemplates) {
      fetchCustomTemplates();
    }
  }, [showTemplates]);

  const handleTemplateMessageOpen = () => {
    setShowTemplates(!showTemplates);
    setIsTemplateMessageOpen(!isTemplateMessageOpen);
  };

  const insertTemplate = (template) => {
    const content = typeof template === 'string' ? template : template.content;
    setMessage(content);
    setShowTemplates(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleCreateTemplate = () => {
    setModalMode('create');
    setEditingTemplate(null);
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setModalMode('edit');
    setEditingTemplate(template);
    setShowTemplateModal(true);
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const response = await templateService.createTemplate({
        name: `${template.name} (Copy)`,
        content: template.content
      });
      
      if (response.success) {
        toast.success("Template duplicated successfully");
        fetchCustomTemplates();
      } else {
        toast.error(response.error?.reason || "Failed to duplicate template");
      }
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast.error("Failed to duplicate template");
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await templateService.deleteTemplate(id);
      
      if (response.success) {
        toast.success("Template deleted successfully");
        fetchCustomTemplates();
      } else {
        toast.error(response.error?.reason || "Failed to delete template");
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleSaveTemplate = async (formData, templateId = null) => {
    try {
      let response;
      
      if (modalMode === 'edit' && templateId) {
        response = await templateService.updateTemplate(templateId, formData);
      } else {
        response = await templateService.createTemplate(formData);
      }
      
      if (response.success) {
        const action = modalMode === 'edit' ? 'updated' : 'created';
        toast.success(`Template ${action} successfully`);
        fetchCustomTemplates();
        setShowTemplateModal(false);
        setEditingTemplate(null);
        return true; // Success
      } else {
        throw new Error(response.error?.reason || `Failed to ${modalMode} template`);
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(error.message || "Failed to save template");
      throw error; // Re-throw for modal to handle
    }
  };

  // Insert variable at cursor position
  const insertVariable = (variable) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = message.substring(0, start);
    const textAfter = message.substring(end);
    const newMessage = textBefore + variable + textAfter;

    setMessage(newMessage);

    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      const newCursorPosition = start + variable.length;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.key === "Enter") {
      e.preventDefault();
      if (isReadyToSend && window.handleQuickSmsSend) {
        window.handleQuickSmsSend();
      }
    }
  };

  const getSmsPartColor = () => {
    if (smsParts === 1) return "text-red-600 bg-red-50 border-red-200";
    if (smsParts === 2) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const getPartBreakdown = () => {
    if (messageLength === 0) return [];
    const parts = [];
    for (let i = 0; i < smsParts; i++) {
      const start = i * smsPartLength;
      const end = Math.min(start + smsPartLength, messageLength);
      const partText = message.slice(start, end);
      
      parts.push({
        number: i + 1,
        text: partText,
        length: partText.length,
        remaining: smsPartLength - partText.length,
      });
    }
    return parts;
  };

  return (
    <div className="p-6 mb-6 bg-white rounded-2xl border border-red-100 shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-red-50 rounded-xl border border-red-200">
            <FiMessageSquare className="text-xl text-red-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Compose Message</h3>
            <p className="text-sm text-gray-600">Write your message content</p>
          </div>
        </div>

        <div className="flex md:flex-col sm:flex-col items-center space-x-2">
          <div>
            <span className="text-sm font-medium text-red-600 mr-2">Templates</span>
          </div>
          <button
            onClick={handleTemplateMessageOpen}
            className={`p-2 rounded-lg transition-all hover:bg-gray-200 ${
              isTemplateMessageOpen 
                ? "bg-red-100 text-red-600 border border-red-200" 
                : "bg-gray-100 text-gray-600 border border-gray-200"
            }`}
            title="Message Templates"
          >
            <ImInsertTemplate className="text-lg" />
          </button>
        </div>
      </div>

      {/* Templates Dropdown */}
      {showTemplates && (
        <div className="p-4 mb-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-gray-800">SMS Templates</h4>
            <button
              onClick={handleCreateTemplate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Add Custom Template
            </button>
          </div>

          <div className="space-y-6">
            {/* Built-in Templates */}
            {templates.map((category, categoryIndex) => (
              <div key={`category-${categoryIndex}`}>
                <h5 className="mb-2 text-sm font-medium text-gray-600">
                  {category.category}
                </h5>
                <div className="grid gap-2">
                  {category.templates.map((template, templateIndex) => (
                    <button
                      key={`builtin-${categoryIndex}-${templateIndex}`}
                      onClick={() => insertTemplate(template)}
                      className="p-3 text-sm text-left text-gray-700 bg-white rounded-lg border border-gray-200 transition-all hover:bg-red-50 hover:border-red-300"
                    >
                      <div className="font-medium text-gray-800 mb-1">
                        {template.name}
                      </div>
                      <div className="text-gray-600">
                        {template.content.length > 100
                          ? template.content.substring(0, 100) + "..."
                          : template.content}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Custom Templates */}
            {customTemplates.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h5 className="text-sm font-medium text-gray-600">
                    Custom Templates ({customTemplates.length})
                  </h5>
                  {loading && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </div>
                  )}
                </div>
                {loading ? (
                  <div className="flex justify-center p-8">
                    <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {customTemplates.map((template) => (
                      <div
                        key={`custom-${template.id}`}
                        className="group relative p-3 text-sm text-left bg-white rounded-lg border border-gray-200 hover:border-red-300 transition-all"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            <div className="font-medium text-gray-800 mb-1 flex items-center gap-2">
                              {template.name}
                              <span className="text-xs px-2 py-0.5 bg-red-50 text-red-700 rounded-full">
                                Custom
                              </span>
                            </div>
                            <div className="text-gray-600 mb-2">
                              {template.content.length > 100
                                ? template.content.substring(0, 100) + "..."
                                : template.content}
                            </div>
                            <div className="text-xs text-gray-400">
                              Created: {new Date(template.created_at).toLocaleDateString()} • 
                              Updated: {new Date(template.updated_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => insertTemplate(template)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Use Template"
                            >
                              <ImInsertTemplate className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditTemplate(template)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit Template"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDuplicateTemplate(template)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Duplicate Template"
                            >
                              <FiCopy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Template"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!loading && customTemplates.length === 0 && (
              <div className="p-8 text-center border-2 border-dashed border-gray-300 rounded-xl">
                <ImInsertTemplate className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h4 className="text-lg font-medium text-gray-600 mb-2">
                  No Custom Templates Yet
                </h4>
                <p className="text-gray-500 mb-4">
                  Create your first custom template to save time on frequent messages
                </p>
                <button
                  onClick={handleCreateTemplate}
                  className="inline-flex items-center gap-2 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FiPlus className="w-4 h-4" />
                  Create Your First Template
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Variable Insertion Buttons - Only shown for Bulk SMS */}
      {showVariables && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-medium text-gray-600">Insert Parameters:</span>
            <button
              type="button"
              onClick={() => insertVariable("{{first_name}}")}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg transition-all hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              title="Insert First Name variable"
            >
              First Name
            </button>
            <button
              type="button"
              onClick={() => insertVariable("{{last_name}}")}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg transition-all hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              title="Insert Last Name variable"
            >
              Last Name
            </button>
            <button
              type="button"
              onClick={() => insertVariable("{{full_name}}")}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg transition-all hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              title="Insert Full Name variable"
            >
              Full Name
            </button>
            <button
              type="button"
              onClick={() => insertVariable("{{phone_number}}")}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg transition-all hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              title="Insert Phone Number variable"
            >
              Phone Number
            </button>
            <button
              type="button"
              onClick={() => insertVariable("{{email}}")}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg transition-all hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              title="Insert Email variable"
            >
              Email
            </button>
          </div>
          <div className="flex gap-2 items-start p-2 mt-2 bg-blue-50 rounded-lg border border-blue-200">
            <svg
              className="flex-shrink-0 w-4 h-4 mt-0.5 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-xs font-medium text-blue-700">
              Variables will be replaced with actual contact data when sending
            </p>
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            isQuickSMS
              ? "Type your message here"
              : "Type your message here... Use the buttons above to insert variables like {{first_name}} and {{last_name}}"
          }
          className="p-4 w-full h-40 placeholder-gray-400 text-gray-700 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all outline-none resize-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
        />

        {/* Character counter and SMS parts overlay */}
        <div className="flex absolute right-4 bottom-4 items-center space-x-2">
          <div
            className={`px-3 py-1 text-sm font-medium rounded-lg border shadow-md ${getSmsPartColor()}`}
          >
            {smsParts} SMS {smsParts === 1 ? "Page" : "Pages"}
          </div>
          <div className="px-3 py-1 bg-white rounded-lg border border-gray-200 shadow-md">
            <span className="text-sm font-medium text-gray-700">
              {messageLength} characters
            </span>
          </div>
        </div>
      </div>

      {/* Message Stats */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mt-4 px-5 py-3.5 bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex gap-3 items-center">
          <div className="flex flex-col">
            <div className="flex gap-1 items-baseline">
              <span className="text-2xl font-bold text-red-600">{messageLength}</span>
              <span className="text-xs font-medium text-gray-400">/160</span>
            </div>
            <span className="text-[10px] font-medium text-gray-500 tracking-wide">Characters</span>
          </div>
          <div className="w-px h-8 bg-gray-200"></div>
        </div>

        <div className="flex gap-3 items-center">
          <div className="flex flex-col">
            <div className="flex gap-1 items-baseline">
              <span className="text-2xl font-bold text-red-600">{smsParts}</span>
              <span className="text-xs font-medium text-gray-400">part{smsParts !== 1 ? 's' : ''}</span>
            </div>
            <span className="text-[10px] font-medium text-gray-500 tracking-wide">SMS Part(s)</span>
          </div>
          <div className="w-px h-8 bg-gray-200"></div>
        </div>

        <div className="flex gap-3 items-center">
          <div className="flex flex-col">
            <div className="flex gap-1 items-baseline">
              <span className="text-2xl font-bold text-red-600">{totalSms}</span>
              <span className="text-xs font-medium text-gray-400">units</span>
            </div>
            <span className="text-[10px] font-medium text-gray-500 tracking-wide">Cost Per Recipient</span>
          </div>
          <div className="w-px h-8 bg-gray-200"></div>
        </div>

        <div className="flex gap-3 items-center">
          <div className="flex flex-col">
            <div className="flex gap-1 items-baseline">
              <span className="text-2xl font-bold text-red-600">
                {messageLength > 0
                  ? smsPartLength - (messageLength % smsPartLength)
                  : smsPartLength}
              </span>
              <span className="text-xs font-medium text-gray-400">left</span>
            </div>
            <span className="text-[10px] font-medium text-gray-500 tracking-wide">Remaining</span>
          </div>
        </div>
      </div>

      {/* External Template Modal */}
      <TemplateModal
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
        }}
        onSave={handleSaveTemplate}
        template={editingTemplate}
        mode={modalMode}
      />
    </div>
  );
};

export default MessageComposer;